import { selectHasMetadata } from '@va/dashboard/selectors/core';
import {
  useAverageSessionDurationFilter,
  useAverageVisitDurationFilter,
  useBrowserNameFilter,
  useBrowserVersionFilter,
  useCitiesFilter,
  useCompanyOrgNameFilter,
  useCompanyOrgTypeFilter,
  useContinuationRateFilter,
  useCountriesFilter,
  useDeviceTypeFilter,
  useDisplayFilter,
  useIpFilter,
  useLandingPageUrlFilter,
  useNewReferrerFilter,
  useNewVisitorCountFilter,
  usePageUrlFilter,
  usePlatformFilter,
  useReturningVisitorCountFilter,
  useSessionCountFilter,
  useTrafficChannelFilter,
  useUserAgentFilter,
  useUtmParametersFilters,
  useVisitCountFilter,
  useVisitMetadataFilters,
  useVisitorCountFilter,
} from '@va/dashboard/shared/filters';
import { useTranslate } from '@va/localization';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const usePageAnalyticsFilters = () => {
  const translate = useTranslate();

  const hasMetadata = useSelector(selectHasMetadata);
  const { nestedFiltersPage } = useVisitMetadataFilters();

  const ipFilter = useIpFilter();
  const osFilter = usePlatformFilter();
  const deviceTypeFilter = useDeviceTypeFilter();
  const displayResolutionFilter = useDisplayFilter();
  const userAgentFilter = useUserAgentFilter();
  const countryFilter = useCountriesFilter();
  const browserNameFilter = useBrowserNameFilter();
  const browserVersionFilter = useBrowserVersionFilter();
  const companyOrgNameFilter = useCompanyOrgNameFilter();
  const companyOrgTypeFilter = useCompanyOrgTypeFilter();
  const cityFilter = useCitiesFilter({
    valueMapper: (value) => {
      const val = Array.isArray(value) ? value[0] : value;
      if (typeof val === 'string' && val.toLowerCase().trim() === translate('labels.unknown').toLowerCase().trim())
        return [''];
      return value;
    },
  });
  const pageUrlFilter = usePageUrlFilter({ allowMultiSelect: true });
  const landingPageFilter = useLandingPageUrlFilter();
  const referrerUrlFilter = useNewReferrerFilter();
  const utmParametersFilters = useUtmParametersFilters();
  const trafficChannelFilters = useTrafficChannelFilter();

  const sessionCountFilter = useSessionCountFilter();
  const visitCountFilter = useVisitCountFilter();
  const visitorCountFilter = useVisitorCountFilter();
  const newVisitorCountFilter = useNewVisitorCountFilter();
  const returningVisitorCountFilter = useReturningVisitorCountFilter();
  const continuationRateFilter = useContinuationRateFilter();
  const avgSessionDurationFilter = useAverageSessionDurationFilter();
  const avgVisitDurationFilter = useAverageVisitDurationFilter();

  const allFilters = useMemo(() => {
    const list = [
      ipFilter,
      osFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      cityFilter,
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      utmParametersFilters,
      pageUrlFilter,
      trafficChannelFilters,
      referrerUrlFilter,
      userAgentFilter,
      landingPageFilter,
      sessionCountFilter,
      visitCountFilter,
      visitorCountFilter,
      newVisitorCountFilter,
      returningVisitorCountFilter,
      continuationRateFilter,
      avgSessionDurationFilter,
      avgVisitDurationFilter,
    ];

    if (hasMetadata) list.push(nestedFiltersPage);

    return list;
  }, [
    ipFilter,
    osFilter,
    deviceTypeFilter,
    displayResolutionFilter,
    cityFilter,
    countryFilter,
    browserNameFilter,
    browserVersionFilter,
    companyOrgNameFilter,
    companyOrgTypeFilter,
    utmParametersFilters,
    pageUrlFilter,
    trafficChannelFilters,
    referrerUrlFilter,
    userAgentFilter,
    landingPageFilter,
    sessionCountFilter,
    visitCountFilter,
    visitorCountFilter,
    newVisitorCountFilter,
    returningVisitorCountFilter,
    continuationRateFilter,
    avgSessionDurationFilter,
    avgVisitDurationFilter,
    hasMetadata,
    nestedFiltersPage,
  ]);

  return useMemo(
    () => ({
      ipFilter,
      osFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      userAgentFilter,
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      cityFilter,
      pageUrlFilter,
      referrerUrlFilter,
      utmParametersFilters,
      trafficChannelFilters,
      landingPageFilter,
      sessionCountFilter,
      visitCountFilter,
      visitorCountFilter,
      newVisitorCountFilter,
      returningVisitorCountFilter,
      continuationRateFilter,
      avgSessionDurationFilter,
      avgVisitDurationFilter,
      allFilters,
    }),
    [
      allFilters,
      ipFilter,
      osFilter,
      deviceTypeFilter,
      displayResolutionFilter,
      userAgentFilter,
      countryFilter,
      browserNameFilter,
      browserVersionFilter,
      companyOrgNameFilter,
      companyOrgTypeFilter,
      cityFilter,
      pageUrlFilter,
      referrerUrlFilter,
      utmParametersFilters,
      trafficChannelFilters,
      landingPageFilter,
      sessionCountFilter,
      visitCountFilter,
      visitorCountFilter,
      newVisitorCountFilter,
      returningVisitorCountFilter,
      continuationRateFilter,
      avgSessionDurationFilter,
      avgVisitDurationFilter,
    ],
  );
};
